import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";

import EntityChart from "../entityChart";
import ReportListView from "../reportListView";
import {
  fetchAdvocacyTimeSpentGraphReport,
  fetchAdvocacyViewsGraphReport,
  fetchAdvocacyShareGraphReport,
  fetchAdvocacyTopSharers,
  fetchAdvocacyTopSharedAssets,
  fetchAdvocacyTopEngagedAssets,
  fetchAdvocacyTopUserGroups,
} from "src/redux/actions/reports";
import {
  ADVOCACY_TOP_SHARED_ASSETS_SCHEMA,
  ADVOCACY_TOP_ENGAGED_ASSETS_SCHEMA,
  ADVOCACY_TOP_SHARERS_SCHEMA,
  ADVOCACY_TOP_SHARED_USER_GROUPS_SCHEMA,
} from "src/constants/reports";
import { AdvocacyTables, ReportTabs } from "src/types/reports";
import TopSharedAssetCard from "../topSharedAssetCard";
import TopEngagedAssetCard from "../topEngagedAssetCard";
import { TopSharersCard } from "../topSharersCard";
import { TopSharedUserGroupCard } from "../topSharedUserGroups";
import { TopSharedAssetListCard } from "../topSharedAssetListCard";
import { TopEngagedAssetListCard } from "../topEngagedAssetListCard";
import { TopSharersListCard } from "../../topSharersListCard";
import { TopSharedUserGroupsListCard } from "../topSharedUserGroupsListCard";

import _ from "lodash";
import styles from "./styles.module.scss";

interface AdvocacyReportTabProps {
  reportDate?: ReportDate;
  filters?: any;
  interval?: string;
}
interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

const AdvocacyTab: React.FC<AdvocacyReportTabProps> = ({
  reportDate,
  filters,
  interval,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { advocacy } = useSelector((state: any) => ({
    advocacy: _.get(state, "reports.advocacy", {}),
  }));
  const isEmployerAdvocacyEnabled = useSelector(state => 
    _.get(state, "accounts.item.employerAdvocacyEnabled", false)
  );

  const {
    sharesGraph,
    viewsGraph,
    timeSpentGraph,
    assetsShared,
    assetsEngaged,
    topSharers,
    topUserGroups,
  } = advocacy;

  const validFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [];

    return filters.filter((filter) => {
      const { property, operator, value } = filter || {};
      return (
        property?.type &&
        operator &&
        Array.isArray(value) &&
        value.length > 0
      );
    });
  }, [filters]);

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: interval || "day",
    };
  }, [reportDate, validFilters, interval]);

  const handleLoadMore = (assetType) => {
    switch (assetType) {
      case "topSharers":
        if (topSharers.pagination.currentPage < topSharers.pagination.totalPages) {
          const request = {
            page: topSharers.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchAdvocacyTopSharers(request));
        }
        break;
      case "topSharedAssets":
        if (assetsShared.pagination.currentPage < assetsShared.pagination.totalPages) {
          const request = {
            page: assetsShared.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchAdvocacyTopSharedAssets(request));
        }
        break;
        
      case "topEngagedAssets":
        if (assetsShared.pagination.currentPage < assetsShared.pagination.totalPages) {
          const request = {
            page: assetsShared.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchAdvocacyTopEngagedAssets(request));
        }
        break;
      case "topUserGroups":
        if (topUserGroups.pagination.currentPage < topUserGroups.pagination.totalPages) {
          const request = {
            page: topUserGroups.pagination.currentPage + 1,
            filter: baseFilters,
            limit: 20,
          };
          dispatch(fetchAdvocacyTopUserGroups(request));
        }
        break;
      default:
        return;
    }
  };

  const handleViewMore = (assetType) => {
    switch (assetType) {
      case "topSharedAssets":
        dispatch(
          fetchAdvocacyTopSharedAssets({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
      
        case "topEngagedAssets":
        dispatch(
          fetchAdvocacyTopEngagedAssets({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "topSharers":
        dispatch(
          fetchAdvocacyTopSharers({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "topUserGroups":
        dispatch(
          fetchAdvocacyTopUserGroups({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      default:
        return;
    }
  };

  useEffect(() => {
    if (!baseFilters?.startDate || !baseFilters?.endDate) return;

    dispatch(fetchAdvocacyTimeSpentGraphReport({ filter: baseFilters }));
    dispatch(fetchAdvocacyViewsGraphReport({ filter: baseFilters }));
    dispatch(fetchAdvocacyShareGraphReport({ filter: baseFilters }));
    dispatch(fetchAdvocacyTopSharers({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchAdvocacyTopSharedAssets({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchAdvocacyTopEngagedAssets({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchAdvocacyTopUserGroups({ page: 0, limit: 5, filter: baseFilters }));
  }, [JSON.stringify(baseFilters)]);

  useEffect(() => {
    if (!isEmployerAdvocacyEnabled) {
      navigate({
        pathname: "/reports/discovery",
        search: `?${createSearchParams(searchParams)}`
      });
    }
  }, [isEmployerAdvocacyEnabled])

  return (
    <div className={styles.cardsContainer}>
      <EntityChart
        title={`# of shares (${sharesGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Daily count of shares made by users on LinkedIn, helping you track content distribution momentum."
        data={sharesGraph?.item}
        legend={{ key: "Date", value: "Shares" }}
        loading={sharesGraph.loading}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.timeSpent}
      />
      <EntityChart
        title={`# of views (${viewsGraph?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Daily count of asset views on LinkedIn, indicating reach and visibility through employee advocacy."
        data={viewsGraph?.item}
        loading={viewsGraph.loading}
        legend={{ key: "Date", value: "Views" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.views}
      />
      <EntityChart
        title={`Daily engagement time`}
        description="Total time viewers spent engaging with shared assets on LinkedIn each day helping you track daily engagement."
        data={timeSpentGraph?.item}
        legend={{ key: "Date", value: "Time Spent" }}
        loading={timeSpentGraph.loading}
        valueFormatter={(value: number) => {
          if (typeof value !== "number" || value <= 0) return "00:00";
          const minutes = Math.floor(value / 60);
          const seconds = Math.round(value % 60);
          return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }}
        yAxisFormatter={(value) => {
          if (typeof value !== "number" || value <= 0) return "00:00";
          const minutes = Math.floor(value / 60);
          const seconds = Math.round(value % 60);
          return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.shares}
      />

      <ReportListView
        title={`Top shared assets (${assetsShared.pagination.totalRecordsCount})`}
        description="Most shared assets on LinkedIn, with supporting metrics on views, reactions, and average time spent."
        canDownload={_.get(assetsShared, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "advocacy_assets",
          schema: ADVOCACY_TOP_SHARED_ASSETS_SCHEMA,
          fileName: "TopSharedAssets",
          title: "Top Shared Assets Report",
          reportType: "topSharedAssets",
        }}
        reportDate={reportDate}
        loading={assetsShared.loading}
        items={assetsShared.items}
        loadMore={() => handleLoadMore("topSharedAssets")}
        viewMore={() => handleViewMore("topSharedAssets")}
        pagination={assetsShared.pagination}
        renderItem={({ item }) => <TopSharedAssetCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <TopSharedAssetListCard item={item} columnCount={columnCount} />
        )}
        columns={[
          "Shares",
          "Views",
          "Reactions",
          "Avg time spent",
          "Total time spent",
          "No of downloads",
          "No of comments",
        ]}
        firstColumn="Assets Shared"
        filters={validFilters}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.topSharers}
      />

      <ReportListView
        title={`Top engaged assets (${assetsEngaged.pagination.totalRecordsCount})`}
        description="Assets ranked by the number of LinkedIn reactions received, helping you identify content that resonated most with your audience."
        canDownload={_.get(assetsEngaged, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "advocacy_engaged_assets",
          schema: ADVOCACY_TOP_ENGAGED_ASSETS_SCHEMA,
          fileName: "TopEngagedAssets",
          title: "Top Engaged Assets Report",
          reportType: "topEngagedAssets",
        }}
        reportDate={reportDate}
        loading={assetsEngaged.loading}
        items={assetsEngaged.items}
        loadMore={() => handleLoadMore("topEngagedAssets")}
        viewMore={() => handleViewMore("topEngagedAssets")}
        pagination={assetsEngaged.pagination}
        renderItem={({ item }) => <TopEngagedAssetCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <TopEngagedAssetListCard item={item} columnCount={columnCount} />
        )}
        columns={[
          "No of reactions",
          "No of comments",
          "Avg. time spent",
          "Total time spent",
          "No of views",
        ]}
        firstColumn="Assets Engaged"
        filters={validFilters}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.topSharedAssets}
      />

      <ReportListView
        title={`Who are your top sharers? (${topSharers.pagination.totalRecordsCount})`}
        description="Identify the most active employees sharing content on LinkedIn, ranked by share count."
        reportDate={reportDate}
        canDownload={_.get(topSharers, 'pagination.totalRecordsCount', 0) <= 5}
        downloadConfig={{
          name: "advocacy_sharers",
          schema: ADVOCACY_TOP_SHARERS_SCHEMA,
          fileName: "TopSharers",
          title: "Top Sharers Report",
          reportType: "topSharers",
        }}
        loading={topSharers.loading}
        items={topSharers.items}
        loadMore={() => handleLoadMore("topSharers")}
        viewMore={() => handleViewMore("topSharers")}
        pagination={topSharers.pagination}
        renderItem={({ item }) => (
          <TopSharersCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <TopSharersListCard item={item} columnCount={columnCount} />
        )}
        columns={["No of shares", "No of unique assets shared"]}
        firstColumn="Top Sharers"
        filters={validFilters}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.topEngagedAssets}
      />

      <ReportListView
        title={`Who are your top user groups? (${topUserGroups.pagination.totalRecordsCount})`}
        description="User groups with the highest cumulative shares on LinkedIn."
        canDownload={_.get(topUserGroups, 'pagination.totalRecordsCount', 0) <= 5}
        reportDate={reportDate}
        downloadConfig={{
          name: "advocacy_user_groups",
          schema: ADVOCACY_TOP_SHARED_USER_GROUPS_SCHEMA,
          fileName: "TopUserGroups",
          title: "Top User Groups Report",
          reportType: "topUserGroups",
        }}
        loading={topUserGroups.loading}
        items={topUserGroups.items}
        loadMore={() => handleLoadMore("topUserGroups")}
        viewMore={() => handleViewMore("topUserGroups")}
        pagination={topUserGroups.pagination}
        renderItem={({ item }) => (
          <TopSharedUserGroupCard item={item} />
        )}
        renderListItem={({ item, columnCount }) => (
          <TopSharedUserGroupsListCard item={item} columnCount={columnCount} />
        )}
        columns={["No of users", "No of shares", "No of unique assets shared"]}
        firstColumn="Top User Groups"
        filters={validFilters}
        tabName={ReportTabs.advocacy}
        tableName={AdvocacyTables.topUserGroups}
      />
    </div>
  );
};

export default AdvocacyTab;
